import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import './Register.css';

export default function Register() {
  const { sendOTPForRegistration, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: '',
    password: '',
    username: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  /* ─── Validation ─── */
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validatePassword = (password) => {
    if (password.length >= 15) return true;
    if (password.length >= 8 && /\d/.test(password) && /[a-z]/.test(password)) return true;
    return false;
  };

  const validateUsername = (username) => /^[a-zA-Z0-9-]+$/.test(username);

  /* ─── Submit ─── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const email = form.email.trim().toLowerCase();
    const username = form.username.trim();

    // Validate
    if (!email) return setError('Email is required.');
    if (!validateEmail(email)) return setError('Please enter a valid email address.');
    if (!form.password) return setError('Password is required.');
    if (!validatePassword(form.password)) {
      return setError(
        'Password must be at least 15 characters OR at least 8 characters including a number and a lowercase letter.'
      );
    }
    if (!username) return setError('Username is required.');
    if (!validateUsername(username)) {
      return setError('Username may only contain alphanumeric characters or hyphens.');
    }

    setLoading(true);
    try {
      await sendOTPForRegistration(username, email, form.password);
      // OTP page pe navigate with email
      navigate('/verify-register-otp', { state: { email } });
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        'Registration failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  /* ─── Google Login ─── */
  const handleGoogleLogin = async (credentialResponse) => {
    setError('');
    setLoading(true);
    try {
      await loginWithGoogle(credentialResponse.credential);
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err.response?.data?.error ||
        'Google sign-in failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card auth-card-wide">

        {/* ─── Brand ─── */}
        <div className="auth-brand">
          <Link to="/" className="auth-logo">
            VK<span>TREND</span>
          </Link>
        </div>

        {/* ─── Title ─── */}
        <h1 className="auth-title">Sign up for VKTrend</h1>

        {/* ─── Error ─── */}
        {error && <div className="auth-error">{error}</div>}

        {/* ─── Form ─── */}
        <form onSubmit={handleSubmit} className="auth-form">
          <label className="auth-label required" htmlFor="reg-email">
            Email
          </label>
          <input
            id="reg-email"
            type="email"
            className="auth-input"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            autoComplete="email"
            autoFocus
            disabled={loading}
          />

          <label className="auth-label required" htmlFor="reg-password">
            Password
          </label>
          <input
            id="reg-password"
            type="password"
            className="auth-input"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            autoComplete="new-password"
            disabled={loading}
          />
          <p className="auth-hint">
            Password should be at least 15 characters OR at least 8 characters
            including a number and a lowercase letter.
          </p>

          <label className="auth-label required" htmlFor="reg-username">
            Username
          </label>
          <input
            id="reg-username"
            type="text"
            className="auth-input"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            autoComplete="username"
            disabled={loading}
          />
          <p className="auth-hint">
            Username may only contain alphanumeric characters or hyphens.
          </p>

          <button
            type="submit"
            className="auth-btn auth-btn-primary"
            disabled={loading}
          >
            {loading ? 'Sending OTP…' : 'Create account'}
          </button>
        </form>

        {/* ─── Divider ─── */}
        <div className="auth-divider">
          <span>or</span>
        </div>

        {/* ─── Google Button ─── */}
        <div className="auth-google">
          <GoogleLogin
            onSuccess={handleGoogleLogin}
            onError={() => setError('Google sign-in failed.')}
            theme="outline"
            size="large"
            text="signup_with"
            shape="rectangular"
 
          />
        </div>

        {/* ─── Footer ─── */}
        <p className="auth-footer">
          Already have an account?{' '}
          <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}